import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, FileText, Download, Calendar, DollarSign, Settings, FileDown, BookOpen } from 'lucide-react';

function ContabilidadFiscal({ user }) {
  const navigate = useNavigate();
  const [ejercicio, setEjercicio] = useState(new Date().getFullYear());
  const [trimestre, setTrimestre] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadResumenFiscal = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/contabilidad/fiscal/resumen', {
        params: { ejercicio, trimestre }
      });
      setResumen(response.data.resumen);
    } catch (error) {
      console.error('Error cargando resumen fiscal:', error);
      toast.error('Error al cargar el resumen fiscal');
    } finally {
      setLoading(false);
    }
  }, [ejercicio, trimestre]);

  useEffect(() => {
    loadResumenFiscal();
  }, [loadResumenFiscal]);

  const handleExportTrimestre = async () => {
    setExporting(true);
    try {
      const response = await axiosInstance.get('/contabilidad/fiscal/export-trimestre', {
        params: { ejercicio, trimestre },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trimestre_${ejercicio}_T${trimestre}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Trimestre ${ejercicio}-T${trimestre} exportado correctamente`);
    } catch (error) {
      console.error('Error exportando trimestre:', error);
      toast.error('Error al exportar el trimestre');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  if (loading && !resumen) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando datos fiscales...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-blue-600" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">
                Contabilidad C10
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/contabilidad/asientos')}
                variant="outline"
                size="sm"
              >
                Asientos
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/facturas-recibidas')}
                variant="outline"
                size="sm"
              >
                Fact. Recibidas
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/libros')}
                variant="outline"
                size="sm"
              >
                Libros
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/ocr')}
                variant="outline"
                size="sm"
              >
                OCR
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/config')}
                variant="outline"
                size="sm"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-base text-gray-600">
            Dashboard Fiscal - Modelos 303 y 130 del trimestre
          </p>
        </div>

        {/* Selector de Periodo */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Periodo Fiscal</CardTitle>
            <CardDescription>
              Selecciona el ejercicio y trimestre para ver los datos fiscales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label htmlFor="ejercicio">Ejercicio</Label>
                <select
                  id="ejercicio"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                  value={ejercicio}
                  onChange={(e) => setEjercicio(parseInt(e.target.value))}
                >
                  {[2025, 2024, 2023, 2022].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <Label htmlFor="trimestre">Trimestre</Label>
                <select
                  id="trimestre"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                  value={trimestre}
                  onChange={(e) => setTrimestre(parseInt(e.target.value))}
                >
                  <option value={1}>1º Trimestre (Ene-Mar)</option>
                  <option value={2}>2º Trimestre (Abr-Jun)</option>
                  <option value={3}>3º Trimestre (Jul-Sep)</option>
                  <option value={4}>4º Trimestre (Oct-Dic)</option>
                </select>
              </div>
              <Button
                onClick={handleExportTrimestre}
                disabled={exporting}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                {exporting ? 'Exportando...' : 'Exportar Trimestre'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {resumen && (
          <>
            {/* Cards de Métricas Principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Facturas Emitidas */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">
                    Facturas Emitidas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {resumen.modelo_303.iva_repercutido.num_facturas}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {formatCurrency(resumen.modelo_303.iva_repercutido.base)}
                  </div>
                </CardContent>
              </Card>

              {/* Facturas Recibidas */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">
                    Facturas Recibidas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {resumen.modelo_303.iva_soportado.num_facturas}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {formatCurrency(resumen.modelo_303.iva_soportado.base)}
                  </div>
                </CardContent>
              </Card>

              {/* IVA a Pagar/Devolver */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">
                    IVA (Modelo 303)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold flex items-center gap-2 ${
                    resumen.modelo_303.resultado.diferencia > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {resumen.modelo_303.resultado.diferencia > 0 ? (
                      <TrendingUp className="h-5 w-5" />
                    ) : (
                      <TrendingDown className="h-5 w-5" />
                    )}
                    {formatCurrency(resumen.modelo_303.resultado.importe)}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {resumen.modelo_303.resultado.tipo === 'a_ingresar' ? 'A ingresar' : 
                     resumen.modelo_303.resultado.tipo === 'a_devolver' ? 'A devolver' : 'Neutro'}
                  </div>
                </CardContent>
              </Card>

              {/* IRPF a Pagar */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">
                    IRPF (Modelo 130)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-600">
                    {formatCurrency(resumen.modelo_130.pago_fraccionado)}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Pago fraccionado 20%
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Modelo 303 - Detalle */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Modelo 303 - Autoliquidación IVA
                </CardTitle>
                <CardDescription>
                  Resumen del IVA del {resumen.periodo}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-blue-900 mb-2">IVA Repercutido (Devengado)</h3>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Base Imponible:</span>
                          <span className="font-semibold">{formatCurrency(resumen.modelo_303.iva_repercutido.base)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Cuota IVA:</span>
                          <span className="font-semibold">{formatCurrency(resumen.modelo_303.iva_repercutido.cuota)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Nº Facturas:</span>
                          <span>{resumen.modelo_303.iva_repercutido.num_facturas}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-green-900 mb-2">IVA Soportado (Deducible)</h3>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Base Imponible:</span>
                          <span className="font-semibold">{formatCurrency(resumen.modelo_303.iva_soportado.base)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Cuota IVA:</span>
                          <span className="font-semibold">{formatCurrency(resumen.modelo_303.iva_soportado.cuota)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Nº Facturas:</span>
                          <span>{resumen.modelo_303.iva_soportado.num_facturas}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200">
                    <h3 className="font-semibold text-yellow-900 mb-2">Resultado</h3>
                    <div className="flex justify-between items-center">
                      <span className="text-lg">
                        {resumen.modelo_303.resultado.tipo === 'a_ingresar' ? 'A INGRESAR' : 
                         resumen.modelo_303.resultado.tipo === 'a_devolver' ? 'A DEVOLVER' : 'NEUTRO'}
                      </span>
                      <span className="text-2xl font-bold">
                        {formatCurrency(resumen.modelo_303.resultado.importe)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Modelo 130 - Detalle */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-orange-600" />
                  Modelo 130 - Pago Fraccionado IRPF
                </CardTitle>
                <CardDescription>
                  Rendimientos y pago fraccionado del {resumen.periodo}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-blue-900 mb-2">Ingresos del Trimestre</h3>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Ingresos:</span>
                          <span className="font-semibold">{formatCurrency(resumen.modelo_130.ingresos.trimestre)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Nº Facturas:</span>
                          <span>{resumen.modelo_130.ingresos.num_facturas}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-red-900 mb-2">Gastos del Trimestre</h3>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Gastos:</span>
                          <span className="font-semibold">{formatCurrency(resumen.modelo_130.gastos.trimestre)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Nº Facturas:</span>
                          <span>{resumen.modelo_130.gastos.num_facturas}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Rendimiento Neto</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Rendimiento del Trimestre:</span>
                        <span className="font-semibold">{formatCurrency(resumen.modelo_130.rendimiento_neto)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Rendimiento Trimestres Previos:</span>
                        <span className="font-semibold">{formatCurrency(resumen.modelo_130.rendimiento_previo)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold">
                        <span>Rendimiento Acumulado:</span>
                        <span>{formatCurrency(resumen.modelo_130.rendimiento_acumulado)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
                    <h3 className="font-semibold text-orange-900 mb-2">Pago Fraccionado (20%)</h3>
                    <div className="flex justify-between items-center">
                      <span className="text-lg">PAGO A REALIZAR</span>
                      <span className="text-2xl font-bold">
                        {formatCurrency(resumen.modelo_130.pago_fraccionado)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}

export default ContabilidadFiscal;
